import { t } from '../i18n';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLocalizedBlog as useBlog } from '../hooks/useLocalizedContent';
import BlogCard from '../components/blog/BlogCard';
import { Search } from 'lucide-react';

const Blog = () => {
  const { posts } = useBlog();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [categories, setCategories] = useState<string[]>([]);

  useEffect(() => {
    const allCategories = posts.flatMap(post => post.categories);
    const uniqueCategories = [...new Set(allCategories)];
    setCategories(uniqueCategories);
  }, [posts]);

  const filteredPosts = posts.filter(post => {
    const matchesSearch = searchQuery === '' ||
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));

    const matchesCategory = selectedCategory === null ||
      post.categories.includes(selectedCategory);

    return matchesSearch && matchesCategory;
  });

  return (
    <div className="content-panel">
      {/* Header */}
      <section className="py-8 md:py-12">
        <h1 className="font-display text-fluid-3xl mb-4">Blog</h1>
        <p className="text-fluid-lg text-foreground/70 max-w-2xl">
          {t("Thoughts, insights, and discoveries from my exploration of technology and development.")}
        </p>
      </section>

      {/* Search and Filters */}
      <section className="py-4 space-y-4">
        {/* Search Bar */}
        <div className="relative max-w-md">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-foreground/50" />
          </div>
          <input
            type="text"
            className="input pl-10 w-full"
            placeholder={t("Search articles...")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {/* Category Filters */}
        <div className="flex flex-wrap gap-2">
          <button
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
              selectedCategory === null
                ? 'bg-primary text-white'
                : 'bg-card border border-foreground/10 text-foreground/70 hover:text-foreground hover:border-foreground/20'
            }`}
            onClick={() => setSelectedCategory(null)}
          >
            {t("All")}
          </button>
          {categories.map((category) => (
            <button
              key={category}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                selectedCategory === category
                  ? 'bg-primary text-white'
                  : 'bg-card border border-foreground/10 text-foreground/70 hover:text-foreground hover:border-foreground/20'
              }`}
              onClick={() => setSelectedCategory(category)}
            >
              {category}
            </button>
          ))}
        </div>
      </section>

      {/* Blog Posts */}
      <section className="py-8">
        {filteredPosts.length > 0 ? (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
            {filteredPosts.map((post) => (
              <Link
                key={post.id}
                to={`/blog/${post.slug}`}
                aria-label={`${t("Read")} ${post.title}`}
                className="block h-full rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                <BlogCard post={post} clickable />
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <h3 className="text-xl font-bold mb-4">{t("No articles found")}</h3>
            <p className="text-foreground/70 mb-6">
              {t("Try adjusting your search or filter to find what you're looking for.")}
            </p>
            <button
              onClick={() => {
                setSearchQuery('');
                setSelectedCategory(null);
              }}
              className="btn btn-primary"
            >
              {t("Clear Filters")}
            </button>
          </div>
        )}
      </section>
    </div>
  );
};

export default Blog;
